import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Plus } from 'lucide-react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { adminService, AiCreditsStatement } from '../../services/adminService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  userLabel?: string;
  onChanged?: () => void;
}

export const formatarReais = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const formatarTokens = (n: number) => n.toLocaleString('pt-BR');

const TIPOS: Record<string, string> = {
  credit: 'Crédito',
  postpaid_on: 'Pós-pago ligado',
  postpaid_off: 'Pós-pago desligado',
};

/**
 * Créditos de IA de uma conta (super-admin): saldo, pós-pago, recarga, consumo por mês e por
 * funcionalidade. Os membros da equipe consomem do saldo do dono da conta.
 */
const AiCreditsModal: React.FC<Props> = ({ isOpen, onClose, userId, userLabel, onChanged }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dados, setDados] = useState<AiCreditsStatement | null>(null);
  const [form, setForm] = useState({ reais: '', notes: '' });
  // Limite a mais: franquia mensal própria da conta, em reais (vazio = a do plano)
  const [franquia, setFranquia] = useState('');

  const carregar = async (id: string) => {
    setLoading(true);
    try {
      const res = await adminService.getAiCredits(id);
      if (res.success && res.data) setDados(res.data);
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !userId) return;
    setDados(null);
    setForm({ reais: '', notes: '' });
    setFranquia('');
    carregar(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  const handleAdd = async () => {
    if (!userId) return;
    const centavos = Math.round(parseFloat(form.reais.replace(',', '.')) * 100);
    if (!centavos || centavos <= 0) {
      toast.error(t('admin.aiCreditsInvalidAmount', 'Informe um valor em reais maior que zero'));
      return;
    }
    setSaving(true);
    try {
      const res = await adminService.addAiCredits(userId, { amount_cents: centavos, notes: form.notes || undefined });
      if (res.success) {
        toast.success(t('admin.aiCreditsAdded', 'Créditos de IA adicionados: {{valor}}', { valor: formatarReais(centavos) }));
        setForm({ reais: '', notes: '' });
        await carregar(userId);
        onChanged?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handlePostpaid = async (postpaid: boolean) => {
    if (!userId) return;
    setSaving(true);
    try {
      const res = await adminService.setAiPostpaid(userId, { postpaid, notes: form.notes || undefined });
      if (res.success) {
        toast.success(postpaid ? t('admin.aiPostpaidOn', 'Conta pós-paga: a IA nunca é bloqueada e o consumo vai no boleto') : t('admin.aiPostpaidOff', 'Conta pré-paga: a IA para quando o saldo zerar'));
        await carregar(userId);
        onChanged?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleLimit = async (voltarAoPlano: boolean) => {
    if (!userId) return;
    const centavos = voltarAoPlano ? null : Math.round(parseFloat(franquia.replace(',', '.')) * 100);
    if (!voltarAoPlano && (!Number.isFinite(centavos as number) || (centavos as number) < 0)) {
      toast.error(t('admin.aiLimitInvalid', 'Informe a franquia mensal em reais'));
      return;
    }
    setSaving(true);
    try {
      const res = await adminService.setAiMonthlyLimit(userId, { monthly_cents: centavos, notes: form.notes || undefined });
      if (res.success && res.data) {
        toast.success(t('admin.aiLimitSaved', 'Franquia mensal agora é {{valor}}', { valor: formatarReais(res.data.included_monthly_cents) }));
        setFranquia('');
        await carregar(userId);
        onChanged?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const conta = dados?.account;
  const resumo = dados?.summary;
  const mesAtual = dados?.months[0];
  const funcionalidadesDoMes = (dados?.features || []).filter((f) => f.month === mesAtual?.month);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('admin.aiCreditsTitle', 'Créditos de IA')} size="2xl" maxHeight>
      <div className="space-y-5">
        {userLabel && <p className="text-sm text-gray-600 dark:text-gray-400">{userLabel}</p>}
        {loading || !dados ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {!dados.billing_active && (
              <p className="text-xs rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 px-3 py-2">
                {t('admin.aiBillingShadow', 'A cobrança começa em {{data}}. Até lá o consumo só é registrado: nada é descontado nem bloqueado.', { data: new Date(dados.billing_start).toLocaleDateString('pt-BR') })}
              </p>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.aiBalance', 'Saldo')}</p>
                <p className={`text-2xl font-bold ${conta && conta.balance_cents < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{formatarReais(conta?.balance_cents || 0)}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.aiThisMonth', 'Consumo no mês')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatarReais(mesAtual?.value_cents || 0)}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.aiAccountType', 'Tipo')}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{conta?.postpaid ? t('admin.aiPostpaid', 'Pós-paga') : t('admin.aiPrepaid', 'Pré-paga')}</p>
              </div>
            </div>

            {resumo && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">{t('admin.aiMonthlyAllowance', 'Franquia mensal de IA')}</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('admin.aiAllowanceNow', 'Este mês: resta {{resta}} de {{total}} · comprado {{comprado}} · disponível {{disp}}', {
                    resta: formatarReais(resumo.included_remaining_cents),
                    total: formatarReais(resumo.monthly_allowance_cents),
                    comprado: formatarReais(resumo.purchased_balance_cents),
                    disp: formatarReais(resumo.available_cents)
                  })}
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-48">
                    <Input
                      label={t('admin.aiLimitReais', 'Limite a mais (R$ por mês)')}
                      type="number"
                      min="0"
                      step="0.01"
                      value={franquia}
                      onChange={(e) => setFranquia(e.target.value)}
                      placeholder={(resumo.monthly_allowance_cents / 100).toFixed(2)}
                    />
                  </div>
                  <Button onClick={() => handleLimit(false)} disabled={saving || !franquia}>{t('admin.aiLimitSave', 'Salvar franquia')}</Button>
                  <Button variant="ghost" onClick={() => handleLimit(true)} disabled={saving}>{t('admin.aiLimitReset', 'Voltar à do plano')}</Button>
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">{t('admin.aiAddCredits', 'Adicionar créditos')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label={t('admin.aiAmountReais', 'Valor (R$)')}
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.reais}
                  onChange={(e) => setForm({ ...form, reais: e.target.value })}
                  placeholder="200,00"
                />
                <Input
                  label={t('admin.videoCreditsNotes')}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder={t('admin.aiNotesPlaceholder', 'Recarga, cortesia ou pagamento do boleto') || ''}
                />
              </div>
              <div className="flex flex-wrap justify-between items-center gap-3 mt-3">
                <Button variant="ghost" onClick={() => handlePostpaid(!conta?.postpaid)} disabled={saving}>
                  {conta?.postpaid ? t('admin.aiMakePrepaid', 'Voltar para pré-paga') : t('admin.aiMakePostpaid', 'Tornar pós-paga')}
                </Button>
                <Button onClick={handleAdd} disabled={saving || !form.reais} loading={saving}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('admin.aiAddCreditsBtn', 'Adicionar')}
                </Button>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">{t('admin.aiByMonth', 'Consumo por mês')}</h4>
              {dados.months.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                        <th className="py-1 pr-3">{t('admin.aiMonth', 'Mês')}</th>
                        <th className="py-1 pr-3 text-right">{t('admin.aiTokens', 'Tokens')}</th>
                        <th className="py-1 pr-3 text-right">{t('admin.aiValue', 'Valor')}</th>
                        <th className="py-1 text-right">{t('admin.aiCharged', 'Descontado')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {dados.months.map((m) => (
                        <tr key={m.month} className="text-gray-900 dark:text-white">
                          <td className="py-1.5 pr-3">{m.month}</td>
                          <td className="py-1.5 pr-3 text-right">{formatarTokens(m.input_tokens + m.output_tokens)}</td>
                          <td className="py-1.5 pr-3 text-right">{formatarReais(m.value_cents)}</td>
                          <td className="py-1.5 text-right">{formatarReais(m.charged_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {funcionalidadesDoMes.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('admin.aiByFeature', 'Por funcionalidade em {{mes}}', { mes: mesAtual?.month })}</p>
                  <ul className="text-sm space-y-0.5">
                    {funcionalidadesDoMes.map((f) => (
                      <li key={f.feature} className="flex justify-between text-gray-700 dark:text-gray-300">
                        <span>{f.feature} <span className="text-xs text-gray-400">({f.calls})</span></span>
                        <span>{formatarReais(f.value_cents)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">{t('admin.aiHistory', 'Lançamentos')}</h4>
              {!dados.transactions || dados.transactions.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {dados.transactions.map((tx) => (
                    <li key={tx.id} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{TIPOS[tx.type] || tx.type}</p>
                        {tx.description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{tx.description}</p>}
                        <p className="text-xs text-gray-400 dark:text-gray-500">{new Date(tx.created_at).toLocaleString('pt-BR')}</p>
                      </div>
                      {tx.amount_cents !== 0 && (
                        <span className="text-sm font-medium text-green-600 dark:text-green-400">+{formatarReais(tx.amount_cents)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AiCreditsModal;
